/** The admin SPA's notion of "which site am I editing". */
export interface SiteConfig {
  CURRENT_DOMAIN_SITE: string;
  CURRENT_SITE_ID: string;
}

// Dev-only fallback: a site that exists in the local/dev DB and that the dev
// user has rights to. NEVER silently fall back to a (production) tenant that
// does not exist locally — that hides misconfiguration and yields an empty/
// read-only screen.
export const configCurrentSiteloc: SiteConfig = {
  CURRENT_DOMAIN_SITE: "localhost:3001",
  CURRENT_SITE_ID: "5f5e9af16bf26151249d2288",
};

// Mutable singleton — starts with dev fallback, overwritten by initSiteConfig() before first render.
export const configCurrentSite: SiteConfig = {
  CURRENT_DOMAIN_SITE: configCurrentSiteloc.CURRENT_DOMAIN_SITE,
  CURRENT_SITE_ID: configCurrentSiteloc.CURRENT_SITE_ID,
};

/**
 * Resolves the current site by asking the backend which tenant this request
 * belongs to. Must be awaited before the app renders so that api.js's
 * interceptor has the correct siteId. No-op in SSR context.
 *
 * The backend answers from the request itself (server/utils/siteDomain.js):
 * the Host header on a real domain, and its own DEFAULT_SITE_ID on localhost /
 * docker-compose / e2e, where `localhost:3001` matches no site record.
 *
 * This replaces the old REACT_APP_CURRENT_SITE_ID override, which was baked
 * into the bundle at build time and `sed`-substituted at container start
 * (SCRUM-890). The site pin now lives on the backend service, which already
 * owns exactly one site per local stack.
 */
/**
 * Pauses between the attempts of {@link initSiteConfig}: three attempts over
 * ~8 s, enough to ride out a Fargate Spot replacement (~20 s of ALB 503s on
 * staging, 2026-10-28) only partly — a longer outage gets the reload notice.
 */
const SITE_RESOLUTION_RETRY_DELAYS_MS = [2000, 6000];

const LOCAL_HOSTS = ["localhost", "127.0.0.1", "[::1]", "::1"];

/** Thrown for answers a retry cannot change (404, a site-less 200). */
class FinalSiteResolutionError extends Error {}

/**
 * Resolves the site and reports whether the app may render. On localhost /
 * docker-compose the dev fallback is a legitimate answer, so this returns true
 * there whatever happened. On a real host it returns false when no attempt
 * produced a site: the fallback id belongs to no tenant there, and every
 * request would carry it (SCRUM-2384). Network failures and 5xx answers are
 * retried; a 4xx or a site-less answer is not.
 */
export async function initSiteConfig(
  { retryDelaysMs = SITE_RESOLUTION_RETRY_DELAYS_MS }: { retryDelaysMs?: number[] } = {}
): Promise<boolean> {
  if (typeof window === "undefined") return true;

  const isLocal = LOCAL_HOSTS.includes(window.location.hostname);
  let lastError: unknown = null;
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt - 1]));
    }
    try {
      await resolveCurrentSite();
      return true;
    } catch (err) {
      lastError = err;
      if (err instanceof FinalSiteResolutionError) break;
    }
  }
  console.error("[config] Could not resolve current site:", lastError);
  return isLocal;
}

async function resolveCurrentSite(): Promise<void> {
  const res = await fetch("/api/sites/current");
  if (!res.ok) {
    // A backend that cannot reach MongoDB answers 503 with the marker
    // /api/health returns (server/helpers/healthCheck.ts, SCRUM-1432). Name it,
    // because `HTTP 400` — what this line logged before — sent the 2026-09-04
    // staging outage to ECS events and CloudWatch to be diagnosed, while the
    // accurate answer was in the response body all along.
    let body: { checks?: { mongo?: string } } | null = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (body?.checks?.mongo === "disconnected") {
      throw new Error(
        `database unreachable — backend reports checks.mongo=disconnected (HTTP ${res.status})`
      );
    }
    const ErrorClass = res.status >= 500 ? Error : FinalSiteResolutionError;
    throw new ErrorClass(`HTTP ${res.status}`);
  }

  // /api/sites/current wraps the site: { currentSite, pagesCount },
  // mirroring searchByDomain (server controllers/site.js).
  const data: { currentSite?: { _id?: string; domain?: string } } | null =
    await res.json();
  const site = data?.currentSite;
  if (!site?._id) {
    throw new FinalSiteResolutionError("the backend answered without a site");
  }
  configCurrentSite.CURRENT_DOMAIN_SITE =
    site.domain || window.location.hostname;
  configCurrentSite.CURRENT_SITE_ID = site._id;
}
